import { useEffect, useMemo, useState } from 'react'

import {
  Button,
  Card,
  Group,
  NumberFormatter,
  SimpleGrid,
  Skeleton,
  Stack,
  Table,
  Text,
  ThemeIcon,
} from '@mantine/core'
import { IconBuilding, IconCoins } from '@tabler/icons-react'
import { Link } from '@tanstack/react-router'
import dayjs from 'dayjs'

import { usdFormatter } from '@repo/common/helpers/formatters'
import { useAdminFundNavs } from '@repo/common/queries/admin/fund/query_admin_fund_navs'
import type { Fund } from '@repo/common/queries/admin/fund/types'

import { TowerCard } from '@/components/ui/TowerCard'

import { ReturnsSection } from './ReturnsSection'

interface Props {
  fund?: Fund
}

const useCountdown = (targetDate: string | null) => {
  const [timeLeft, setTimeLeft] = useState<string>('')

  useEffect(() => {
    if (!targetDate) return

    const updateCountdown = () => {
      const now = dayjs()
      const target = dayjs(targetDate)
      const diff = target.diff(now)

      if (diff <= 0) {
        setTimeLeft('Published')
        return
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24))
      const hours = Math.floor(
        (diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
      )
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))

      if (days > 0) {
        setTimeLeft(`${days}d ${hours}h`)
      } else if (hours > 0) {
        setTimeLeft(`${hours}h ${minutes}m`)
      } else {
        setTimeLeft(`${minutes}m`)
      }
    }

    updateCountdown()
    const interval = setInterval(updateCountdown, 60000)

    return () => {
      clearInterval(interval)
    }
  }, [targetDate])

  return timeLeft
}

const PublicationCountdown: React.FC<{ fund: Fund }> = ({ fund }) => {
  const countdown = useCountdown(fund.next_publication_at)

  if (!fund.next_publication_at) {
    return <Text c="dimmed">Not scheduled</Text>
  }

  const scheduledTime = dayjs(fund.next_publication_at).format('MMM D, h:mm A')
  const isOfficial = fund.next_publication_official

  return (
    <Stack gap={4}>
      <Text fw={600} size="lg">
        {countdown}
      </Text>
      <Text size="xs" c="dimmed">
        {scheduledTime}
      </Text>
      <Text size="xs" c={isOfficial ? 'green' : 'orange'}>
        {isOfficial ? 'Official' : 'Estimated'}
      </Text>
    </Stack>
  )
}

export const FundSummary: React.FC<Props> = ({ fund }) => {
  const formatDate = (dateString: string) => {
    return dayjs(dateString).format('MMM D, YYYY')
  }

  const sevenDaysAgo = dayjs().subtract(7, 'days').format('YYYY-MM-DD')
  const thirtyDaysAgo = dayjs().subtract(30, 'days').format('YYYY-MM-DD')
  const threeMonthsAgo = dayjs().subtract(3, 'months').format('YYYY-MM-DD')
  const yearStart = dayjs().startOf('year').format('YYYY-MM-DD')
  const currentDate = dayjs().format('YYYY-MM-DD')

  const sevenDayNavs = useAdminFundNavs({
    fundId: fund?.id || '',
    startDate: sevenDaysAgo,
    endDate: currentDate,
  })

  const thirtyDayNavs = useAdminFundNavs({
    fundId: fund?.id || '',
    startDate: thirtyDaysAgo,
    endDate: currentDate,
  })

  const threeMonthNavs = useAdminFundNavs({
    fundId: fund?.id || '',
    startDate: threeMonthsAgo,
    endDate: currentDate,
  })

  const ytdNavs = useAdminFundNavs({
    fundId: fund?.id || '',
    startDate: yearStart,
    endDate: currentDate,
  })

  const sinceInceptionNavs = useAdminFundNavs({
    fundId: fund?.id || '',
    startDate: fund?.created_at
      ? dayjs(fund.created_at).format('YYYY-MM-DD')
      : undefined,
    endDate: currentDate,
  })

  const calculateReturn = (
    startPrice: number | undefined,
    endPrice: number | undefined,
  ): number | null => {
    if (!startPrice || !endPrice || startPrice === 0) return null
    return ((endPrice - startPrice) / startPrice) * 100
  }

  const returnsData = useMemo(() => {
    const currentPrice =
      fund?.live_nav?.price_per_share ||
      fund?.latest_published_nav?.price_per_share

    const sevenDayReturn = calculateReturn(
      sevenDayNavs.data?.navs.nodes[sevenDayNavs.data.navs.nodes.length - 1]
        ?.price_per_share,
      currentPrice,
    )

    const thirtyDayReturn = calculateReturn(
      thirtyDayNavs.data?.navs.nodes[thirtyDayNavs.data.navs.nodes.length - 1]
        ?.price_per_share,
      currentPrice,
    )

    const threeMonthReturn = calculateReturn(
      threeMonthNavs.data?.navs.nodes[threeMonthNavs.data.navs.nodes.length - 1]
        ?.price_per_share,
      currentPrice,
    )

    const ytdReturn = calculateReturn(
      ytdNavs.data?.navs.nodes[ytdNavs.data.navs.nodes.length - 1]
        ?.price_per_share,
      currentPrice,
    )

    const sinceInceptionReturn = calculateReturn(
      fund?.starting_price_per_share,
      currentPrice,
    )

    return [
      {
        period: 'Since Inception',
        returnPercentage: sinceInceptionReturn,
        startDate: fund ? formatDate(fund.created_at) : undefined,
        endDate: 'Current',
      },
      {
        period: '7-Day Rolling',
        returnPercentage: sevenDayReturn,
        startDate: dayjs().subtract(7, 'days').format('MMM D, YYYY'),
        endDate: 'Current',
      },
      {
        period: '30-Day Rolling',
        returnPercentage: thirtyDayReturn,
        startDate: dayjs().subtract(30, 'days').format('MMM D, YYYY'),
        endDate: 'Current',
      },
      {
        period: '3-Month Rolling',
        returnPercentage: threeMonthReturn,
        startDate: dayjs().subtract(3, 'months').format('MMM D, YYYY'),
        endDate: 'Current',
      },
      {
        period: 'Year to Date',
        returnPercentage: ytdReturn,
        startDate: dayjs().startOf('year').format('MMM D, YYYY'),
        endDate: 'Current',
      },
    ]
  }, [
    fund,
    sevenDayNavs.data,
    thirtyDayNavs.data,
    threeMonthNavs.data,
    ytdNavs.data,
  ])

  if (!fund) {
    return (
      <TowerCard
        title="Loading..."
        description="Fund Overview"
        icon={<IconBuilding size={24} />}
      >
        <Stack gap="xl">
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
            {Array.from({ length: 6 }).map((_, index) => (
              <Card key={index} withBorder p="md" radius="md">
                <Stack gap="xs">
                  <Skeleton height={32} width={32} radius="md" />
                  <Skeleton height={14} width={80} />
                  <Skeleton height={20} width={60} />
                </Stack>
              </Card>
            ))}
          </SimpleGrid>

          <Card withBorder>
            <Stack gap="md">
              <Skeleton height={24} width={150} />
              <Stack gap="sm">
                {Array.from({ length: 3 }).map((_, index) => (
                  <Skeleton key={index} height={40} />
                ))}
              </Stack>
            </Stack>
          </Card>

          <Card withBorder>
            <Group>
              <Skeleton height={36} width={160} />
              <Skeleton height={36} width={140} />
            </Group>
          </Card>
        </Stack>
      </TowerCard>
    )
  }

  return (
    <TowerCard
      title={fund.name}
      description="Fund Overview"
      icon={<IconBuilding size={24} />}
    >
      <Stack gap="xl">
        <Card
          withBorder
          p="lg"
          radius="md"
          bg="light-dark(var(--mantine-color-gray-1),var(--mantine-color-dark-7))"
        >
          <Stack gap="md">
            {/*<Group gap="sm">*/}
            {/*  <ThemeIcon color="orange" variant="light" size="lg" radius="md">*/}
            {/*    <IconBuilding size={24} />*/}
            {/*  </ThemeIcon>*/}
            {/*  <Title order={4}>Fund Basics</Title>*/}
            {/*</Group>*/}

            <SimpleGrid cols={{ base: 2, md: 4 }} spacing="md">
              <div>
                <Text size="sm" c="dimmed" mb={4}>
                  Next Publication
                </Text>
                <PublicationCountdown fund={fund} />
              </div>

              <div>
                <Text size="sm" c="dimmed" mb={4}>
                  Management Fee
                </Text>
                <Text fw={600} size="lg">
                  <NumberFormatter
                    value={fund.management_fee_percentage}
                    suffix="%"
                    decimalScale={2}
                  />
                </Text>
              </div>

              <div>
                <Text size="sm" c="dimmed" mb={4}>
                  Created Date
                </Text>
                <Text fw={500} size="lg">
                  {formatDate(fund.created_at)}
                </Text>
              </div>

              <div>
                <Text size="sm" c="dimmed" mb={4}>
                  Starting Price
                </Text>
                <Text fw={600} size="lg">
                  <NumberFormatter
                    value={fund.starting_price_per_share}
                    prefix="$"
                    thousandSeparator
                    decimalScale={4}
                  />
                </Text>
              </div>
            </SimpleGrid>
          </Stack>
        </Card>

        <Card withBorder>
          <Stack gap="md">
            <Group gap="sm">
              <ThemeIcon color="blue" variant="light" size="lg" radius="md">
                <IconCoins size={20} />
              </ThemeIcon>
              <Text fw={600} size="md">
                NAV Summary
              </Text>
            </Group>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>&nbsp;</Table.Th>
                  <Table.Th>Total Valuation</Table.Th>
                  <Table.Th>Price Per Share</Table.Th>
                  <Table.Th>Total Shares</Table.Th>
                  <Table.Th>Assets Value</Table.Th>
                  <Table.Th>Cash Value</Table.Th>
                  <Table.Th>Unminted</Table.Th>
                  <Table.Th>Status</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                <Table.Tr>
                  <Table.Td>
                    <Group gap="sm">
                      {/*<ThemeIcon*/}
                      {/*  color="cyan"*/}
                      {/*  variant="light"*/}
                      {/*  size="sm"*/}
                      {/*  radius="md"*/}
                      {/*>*/}
                      {/*  <IconChartLine size={16} />*/}
                      {/*</ThemeIcon>*/}
                      <Text fw={600}>Live</Text>
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    {fund.live_nav ? (
                      <NumberFormatter
                        value={fund.live_nav.valuation}
                        prefix="$"
                        thousandSeparator
                        decimalScale={2}
                      />
                    ) : (
                      <Text c="dimmed">-</Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    {fund.live_nav ? (
                      <NumberFormatter
                        value={fund.live_nav.price_per_share}
                        prefix="$"
                        decimalScale={4}
                      />
                    ) : (
                      <Text c="dimmed">-</Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    {fund.live_nav ? (
                      <NumberFormatter
                        value={fund.live_nav.total_shares}
                        thousandSeparator
                        decimalScale={0}
                      />
                    ) : (
                      <Text c="dimmed">-</Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    {fund.live_nav ? (
                      <NumberFormatter
                        value={fund.live_nav.assets_total_value}
                        prefix="$"
                        thousandSeparator
                        decimalScale={2}
                      />
                    ) : (
                      <Text c="dimmed">-</Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    {fund.live_nav ? (
                      <NumberFormatter
                        value={fund.live_nav.cash_total_value}
                        prefix="$"
                        thousandSeparator
                        decimalScale={2}
                      />
                    ) : (
                      <Text c="dimmed">-</Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    {fund.live_nav ? (
                      <Stack gap={2}>
                        <Text size="sm" fw={500}>
                          <NumberFormatter
                            value={fund.live_nav.pending_shares}
                            thousandSeparator
                            decimalScale={0}
                            suffix=" shares"
                          />
                        </Text>
                        <Text size="xs" c="dimmed">
                          <NumberFormatter
                            value={fund.live_nav.cash_pending_value}
                            prefix="$"
                            thousandSeparator
                            decimalScale={2}
                          />
                        </Text>
                      </Stack>
                    ) : (
                      <Text c="dimmed">-</Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    {fund.live_nav?.effective_at ? (
                      <Text size="sm" c="dimmed">
                        Effective{' '}
                        {dayjs(fund.live_nav.effective_at).format(
                          'MMM D, h:mm A',
                        )}
                      </Text>
                    ) : (
                      <Text c="dimmed">-</Text>
                    )}
                  </Table.Td>
                </Table.Tr>

                <Table.Tr>
                  <Table.Td>
                    <Group gap="sm">
                      {/*<ThemeIcon*/}
                      {/*  color="blue"*/}
                      {/*  variant="light"*/}
                      {/*  size="sm"*/}
                      {/*  radius="md"*/}
                      {/*>*/}
                      {/*  <IconCoin size={16} />*/}
                      {/*</ThemeIcon>*/}
                      <Text fw={600}>Latest Published</Text>
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    {fund.latest_published_nav ? (
                      <NumberFormatter
                        value={fund.latest_published_nav.valuation}
                        prefix="$"
                        thousandSeparator
                        decimalScale={2}
                      />
                    ) : (
                      <Text c="dimmed">-</Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    {fund.latest_published_nav ? (
                      <NumberFormatter
                        value={fund.latest_published_nav.price_per_share}
                        prefix="$"
                        decimalScale={4}
                      />
                    ) : (
                      <Text c="dimmed">-</Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    {fund.latest_published_nav ? (
                      <NumberFormatter
                        value={fund.latest_published_nav.total_shares}
                        thousandSeparator
                        decimalScale={0}
                      />
                    ) : (
                      <Text c="dimmed">-</Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    {fund.latest_published_nav ? (
                      <NumberFormatter
                        value={fund.latest_published_nav.assets_total_value}
                        prefix="$"
                        thousandSeparator
                        decimalScale={2}
                      />
                    ) : (
                      <Text c="dimmed">-</Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    {fund.latest_published_nav ? (
                      <NumberFormatter
                        value={fund.latest_published_nav.cash_total_value}
                        prefix="$"
                        thousandSeparator
                        decimalScale={2}
                      />
                    ) : (
                      <Text c="dimmed">-</Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    {fund.latest_published_nav ? (
                      <Stack gap={2}>
                        <Text size="sm" fw={500}>
                          <NumberFormatter
                            value={fund.latest_published_nav.pending_shares}
                            thousandSeparator
                            decimalScale={0}
                            suffix=" shares"
                          />
                        </Text>
                        <Text size="xs" c="dimmed">
                          <NumberFormatter
                            value={fund.latest_published_nav.cash_pending_value}
                            prefix="$"
                            thousandSeparator
                            decimalScale={2}
                          />
                        </Text>
                      </Stack>
                    ) : (
                      <Text c="dimmed">-</Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    {fund.latest_published_nav?.published_at ? (
                      <Stack gap={2}>
                        <Text size="sm" c="green" fw={500}>
                          Published
                        </Text>
                        <Text size="xs" c="dimmed">
                          {dayjs(fund.latest_published_nav.published_at).format(
                            'MMM D, h:mm A',
                          )}
                        </Text>
                      </Stack>
                    ) : (
                      <Text c="dimmed">-</Text>
                    )}
                  </Table.Td>
                </Table.Tr>

                <Table.Tr>
                  <Table.Td>
                    <Group gap="sm">
                      {/*<ThemeIcon*/}
                      {/*  color="green"*/}
                      {/*  variant="light"*/}
                      {/*  size="sm"*/}
                      {/*  radius="md"*/}
                      {/*>*/}
                      {/*  <IconCoinFilled size={16} />*/}
                      {/*</ThemeIcon>*/}
                      <Text fw={600}>Latest Official</Text>
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    {fund.latest_official_nav ? (
                      <NumberFormatter
                        value={fund.latest_official_nav.valuation}
                        prefix="$"
                        thousandSeparator
                        decimalScale={2}
                      />
                    ) : (
                      <Text c="dimmed">-</Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    {fund.latest_official_nav ? (
                      <NumberFormatter
                        value={fund.latest_official_nav.price_per_share}
                        prefix="$"
                        decimalScale={4}
                      />
                    ) : (
                      <Text c="dimmed">-</Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    {fund.latest_official_nav ? (
                      <NumberFormatter
                        value={fund.latest_official_nav.total_shares}
                        thousandSeparator
                        decimalScale={0}
                      />
                    ) : (
                      <Text c="dimmed">-</Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    {fund.latest_official_nav ? (
                      <NumberFormatter
                        value={fund.latest_official_nav.assets_total_value}
                        prefix="$"
                        thousandSeparator
                        decimalScale={2}
                      />
                    ) : (
                      <Text c="dimmed">-</Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    {fund.latest_official_nav ? (
                      <NumberFormatter
                        value={fund.latest_official_nav.cash_total_value}
                        prefix="$"
                        thousandSeparator
                        decimalScale={2}
                      />
                    ) : (
                      <Text c="dimmed">-</Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    {fund.latest_official_nav ? (
                      <Stack gap={2}>
                        <Text size="sm" fw={500}>
                          <NumberFormatter
                            value={fund.latest_official_nav.pending_shares}
                            thousandSeparator
                            decimalScale={0}
                            suffix=" shares"
                          />
                        </Text>
                        <Text size="xs" c="dimmed">
                          <NumberFormatter
                            value={fund.latest_official_nav.cash_pending_value}
                            prefix="$"
                            thousandSeparator
                            decimalScale={2}
                          />
                        </Text>
                      </Stack>
                    ) : (
                      <Text c="dimmed">-</Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    {fund.latest_official_nav?.published_at ? (
                      <Stack gap={2}>
                        <Text size="sm" c="green" fw={500}>
                          Official
                        </Text>
                        <Text size="xs" c="dimmed">
                          {dayjs(fund.latest_official_nav.published_at).format(
                            'MMM D, h:mm A',
                          )}
                        </Text>
                      </Stack>
                    ) : (
                      <Text c="dimmed">-</Text>
                    )}
                  </Table.Td>
                </Table.Tr>
              </Table.Tbody>
            </Table>
          </Stack>
        </Card>

        <ReturnsSection
          returns={returnsData}
          loading={
            sevenDayNavs.isLoading ||
            thirtyDayNavs.isLoading ||
            threeMonthNavs.isLoading ||
            ytdNavs.isLoading ||
            sinceInceptionNavs.isLoading
          }
        />

        {fund.feeders && fund.feeders.length > 0 && (
          <Card withBorder>
            <Stack gap="md">
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Token</Table.Th>
                    <Table.Th>Name</Table.Th>
                    <Table.Th>Type</Table.Th>
                    <Table.Th>Visibility</Table.Th>
                    <Table.Th>Price Per Share</Table.Th>
                    <Table.Th>NAV</Table.Th>
                    <Table.Th>Unminted Deposits</Table.Th>
                    <Table.Th></Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {fund.feeders.map((feeder) => (
                    <Table.Tr key={feeder.id}>
                      <Table.Td>
                        <Text fw={600}>{feeder.symbol}</Text>
                      </Table.Td>
                      <Table.Td>{feeder.name}</Table.Td>
                      <Table.Td>
                        <Text size="sm">
                          {feeder.tradable ? 'Tradable' : 'Simulated'}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">
                          {feeder.published ? 'Public' : 'Hidden'}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        {usdFormatter(feeder.live_nav?.price_per_share)}
                      </Table.Td>
                      <Table.Td>
                        {usdFormatter(feeder.live_nav?.valuation)}
                      </Table.Td>
                      <Table.Td>
                        {usdFormatter(feeder.unminted_deposit_total)}
                      </Table.Td>
                      <Table.Td>
                        <Link
                          to="/fund/$id/$token/record-deposit"
                          params={{ id: fund.id, token: feeder.symbol }}
                        >
                          <Button
                            leftSection={<IconCoins size={16} />}
                            color="blue"
                            size="sm"
                          >
                            Record Deposit
                          </Button>
                        </Link>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Stack>
          </Card>
        )}
      </Stack>
    </TowerCard>
  )
}
